import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Play, Sparkles, CheckCircle, AlertCircle, Zap } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `há ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

const DOMAIN_LABELS = {
  sales: "Vendas", menu: "Menu", finance: "Finanças",
  reliability: "Fiabilidade", customer_identity: "Clientes",
};

const BANNER_REASON_STEP = {
  flags_off:                    1,
  no_events_observed:           2,
  rebuild_not_run:              3,
  no_recommendations_generated: 4,
  top_recommendation_available: 5,
  top_recommendation_null:      4,
};

const PRIORITY_CONFIG = {
  high:   { label: "Alta prioridade",  cls: "bg-amber-500/15 text-amber-300 border border-amber-500/20" },
  medium: { label: "Média prioridade", cls: "bg-slate-700/60 text-slate-300 border border-slate-600/40" },
  low:    { label: "Baixa prioridade", cls: "bg-slate-800/60 text-slate-400 border border-slate-700/40" },
};

function StatusDot({ on }) {
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${on ? "bg-emerald-500" : "bg-slate-600"}`} />;
}

function PipelineStep({ n, active, done, label, hint }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5
        ${done ? "bg-emerald-500/20 text-emerald-400" : active ? "bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40" : "bg-slate-800 text-slate-600"}`}>
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${done ? "text-slate-500" : active ? "text-slate-200" : "text-slate-600"}`}>
          {label}
        </p>
        {active && hint && <p className="text-[11px] text-amber-400/80 mt-0.5 leading-relaxed">{hint}</p>}
      </div>
      {done && <CheckCircle className="w-3.5 h-3.5 text-emerald-500/70 shrink-0 mt-0.5" />}
    </div>
  );
}

export function KarolAlertaPanel({ authAxios, restaurantId }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildMsg, setRebuildMsg] = useState(null);

  const load = useCallback(() => {
    if (!authAxios || !restaurantId) return;
    setLoading(true);
    authAxios
      .get(`/karol/diagnostics/${restaurantId}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [authAxios, restaurantId]);

  useEffect(() => { load(); }, [load]);

  const doRebuild = () => {
    setRebuilding(true);
    setRebuildMsg(null);
    authAxios
      .post(`/karol/insights/${restaurantId}/rebuild`)
      .then(r => {
        const errs = r.data?.partial_errors;
        setRebuildMsg(errs?.length ? `Concluído com erros: ${errs.join(", ")}` : "Rebuild concluído.");
        setTimeout(load, 800);
      })
      .catch(() => setRebuildMsg("Erro no rebuild."))
      .finally(() => setRebuilding(false));
  };

  if (loading) return (
    <div className="rounded-2xl border border-slate-700/60 bg-slate-900 p-5 mb-6 flex items-center gap-2 text-slate-500 text-sm">
      <RefreshCw className="w-4 h-4 animate-spin" /> A carregar…
    </div>
  );

  if (!data || data.error) return (
    <div className="rounded-2xl border border-slate-700/60 bg-slate-900 p-5 mb-6">
      <p className="text-sm text-slate-500">
        {data?.error === "diagnostics_failed"
          ? "Erro no diagnóstico — verifica logs do servidor."
          : "Karol não disponível — verifica flags e permissões."}
      </p>
    </div>
  );

  const { flags, events, rebuild: rb, recommendations: recs, top_recommendation: topRec, banner } = data;
  const step = BANNER_REASON_STEP[banner?.reason] ?? 0;
  const isActive = step === 5;
  const priority = (topRec?.priority || "low").toLowerCase();
  const pCfg = PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG.low;

  return (
    <div className="rounded-2xl border border-slate-700/60 bg-slate-900 mb-6 overflow-hidden" data-testid="karol-alerta-panel">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-slate-100">Karol Central</span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isActive ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
            {isActive ? "Activo" : `Passo ${step > 0 ? step : 1}/5`}
          </span>
        </div>
        <button onClick={load} disabled={loading}
          className="p-1.5 text-slate-500 hover:text-slate-300 transition-colors rounded-lg hover:bg-slate-800">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="p-5 space-y-5">

        {/* Top recommendation — hero */}
        {topRec ? (
          <div className={`rounded-xl px-4 py-4 bg-gradient-to-br from-amber-500/8 via-slate-800/60 to-slate-900 border ${priority === "high" ? "border-amber-500/25" : "border-slate-700/60"}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <Zap className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pCfg.cls}`}>{pCfg.label}</span>
              <span className="text-[10px] text-slate-600 ml-auto">{topRec.source}</span>
            </div>
            <p className="text-base font-semibold text-slate-100 leading-snug">{topRec.title}</p>
            {topRec.description && (
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{topRec.description}</p>
            )}
          </div>
        ) : (
          <div className="rounded-xl bg-slate-800/40 border border-slate-700/60 px-4 py-4">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-slate-400 mb-0.5">Sem recomendação activa</p>
                <p className="text-xs text-slate-500 leading-relaxed">{banner?.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Sistema */}
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Sistema</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: "observer_enabled", label: "Observer" },
              { key: "operational_ui_enabled", label: "UI operacional" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2 bg-slate-800/60 rounded-xl px-3 py-2.5">
                <StatusDot on={flags[key]} />
                <span className="text-xs text-slate-300 flex-1">{label}</span>
                <span className={`text-[10px] font-bold ${flags[key] ? "text-emerald-400" : "text-slate-500"}`}>
                  {flags[key] ? "ON" : "OFF"}
                </span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="bg-slate-800/60 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-slate-500 mb-0.5">Eventos</p>
              <p className={`text-2xl font-bold leading-none ${events.total > 0 ? "text-slate-200" : "text-slate-600"}`}>
                {events.total}
              </p>
              <p className="text-[10px] text-slate-500 mt-1 leading-tight">
                {events.last_event_type
                  ? `${events.last_event_type} · ${timeAgo(events.last_event_at)}`
                  : "nenhum evento ainda"}
              </p>
            </div>
            <div className="bg-slate-800/60 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-slate-500 mb-0.5">Último rebuild</p>
              <p className={`text-sm font-semibold ${rb.has_run ? "text-slate-200" : "text-slate-600"}`}>
                {rb.has_run ? timeAgo(rb.last_rebuilt_at) : "Nunca"}
              </p>
              {!rb.has_run && events.total > 0 && (
                <p className="text-[10px] text-amber-400 mt-1">Usa rebuild abaixo</p>
              )}
            </div>
          </div>
        </div>

        {/* Recomendações por domínio */}
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Recomendações por domínio</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
              <div key={key} className="bg-slate-800/60 rounded-xl px-2 py-2.5 text-center">
                <p className={`text-xl font-bold ${(recs[key] ?? 0) > 0 ? "text-amber-400" : "text-slate-600"}`}>
                  {recs[key] ?? 0}
                </p>
                <p className="text-[9px] text-slate-500 leading-tight mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Pipeline */}
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Pipeline</p>
          <div className="space-y-2.5">
            <PipelineStep n={1} done={step > 1} active={step === 1} label="Flags ON" hint="Activar KAROL_OBSERVER_ENABLED e KAROL_OPERATIONAL_UI_ENABLED no Render." />
            <PipelineStep n={2} done={step > 2} active={step === 2} label="Eventos observados" hint="Gera um pedido real para activar o observer." />
            <PipelineStep n={3} done={step > 3} active={step === 3} label="Rebuild executado" hint="Clica em Rebuild abaixo." />
            <PipelineStep n={4} done={step > 4} active={step === 4} label="Recomendações geradas" hint="Restaurante pode não ter sinais activos." />
            <PipelineStep n={5} done={step === 5} active={false} label="Banner activo em /orders" hint="" />
          </div>
        </div>

        {/* Banner status */}
        <div className={`rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 border ${
          banner.should_show ? "bg-emerald-500/8 border-emerald-500/25" : "bg-slate-800/40 border-slate-700/60"}`}>
          {banner.should_show
            ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            : <AlertCircle className="w-4 h-4 text-slate-500 shrink-0" />}
          <p className="text-xs text-slate-400 leading-relaxed">{banner.message}</p>
        </div>

        {/* Rebuild */}
        <div className="border-t border-slate-700/50 pt-4">
          <button onClick={doRebuild} disabled={rebuilding}
            className="w-full flex items-center justify-center gap-2 text-xs font-medium text-slate-300 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-600/50 hover:border-slate-500 rounded-xl px-4 py-2.5 transition-colors disabled:opacity-50">
            <Play className="w-3 h-3" />
            {rebuilding ? "A reconstruir…" : "Forçar rebuild agora"}
          </button>
          {rebuildMsg && <p className="text-xs text-center text-slate-400 mt-2">{rebuildMsg}</p>}
        </div>

      </div>
    </div>
  );
}
